import {
  ArrowUpRight,
  GraduationCap,
  FileText,
  MessagesSquare,
  Brain,
  Compass,
  Sprout,
  Trophy,
  ClipboardCheck,
  Gauge,
  Route,
  ShieldCheck,
  UserCheck,
  BookOpenCheck,
  Target,
  CheckCircle2,
} from "lucide-react";

import SEO from "@/components/site/SEO";
import PageHero from "@/components/site/PageHero";
import CTASection from "@/components/site/CTASection";
import FAQSection from "@/components/site/FAQSection";

const JOURNEY_STEPS = [
  {
    icon: Compass,
    title: "1. Understand the role",
    body:
      "Before applying, you need to understand what the train driver role actually involves. It is not just about driving trains. It is a safety-critical role built around concentration, rules knowledge, communication, decision making, personal discipline and the ability to manage risk calmly under pressure.",
  },
  {
    icon: FileText,
    title: "2. Build a stronger application",
    body:
      "Your CV and application need to show more than enthusiasm. They need to evidence transferable skills, safety awareness, reliability, communication, concentration, rule-following, judgement and motivation for the railway. We help candidates explain their experience in a way that makes sense to rail recruiters.",
  },
  {
    icon: Brain,
    title: "3. Prepare for psychometrics",
    body:
      "Train driver psychometric assessments are designed to explore safety-critical aptitudes such as attention, vigilance, perception, reaction, memory, coordination and rules-based reasoning. Preparation should be ethical, structured and focused on familiarity, confidence and consistency.",
  },
  {
    icon: MessagesSquare,
    title: "4. Prepare for interview",
    body:
      "Train driver interviews often explore motivation, safety behaviour, communication, concentration, responsibility and how you deal with pressure. Good preparation helps you give clear, relevant examples rather than vague or generic answers.",
  },
  {
    icon: ShieldCheck,
    title: "5. Develop operational thinking",
    body:
      "The strongest candidates do not just memorise answers. They begin to think like safety-critical professionals. That means recognising risk, following procedure, communicating clearly, avoiding shortcuts and understanding why railway standards exist.",
  },
];

const INDIVIDUAL_SUPPORT = [
  {
    icon: GraduationCap,
    title: "I want to become a train driver",
    body:
      "Start with the Train Driver Foundation route. This is where you will find structured support for applications, CVs, interviews, trainee driver preparation and career planning.",
    cta: "Go to Train Driver Foundation",
    href: "https://www.traindriverfoundation.com",
    external: true,
  },
  {
    icon: Brain,
    title: "I need psychometric practice",
    body:
      "Use Train Driver Psychometrics UK for structured familiarisation and practice tools. This is the best route if you are preparing for attention, vigilance, perception, coordination and reaction-style assessments.",
    cta: "Go to Train Driver Psychometrics UK",
    href: "https://traindriverpsychometrics.co.uk",
    external: true,
  },
  {
    icon: Sprout,
    title: "I am 16–18 and planning ahead",
    body:
      "Use the Future Driver pathway if you are not yet ready to apply but want to start building evidence, operational maturity and a better understanding of the railway as a career.",
    cta: "Explore the Future Driver pathway",
    href: "https://traindriverfoundation.com/become-a-train-driver-at-18",
    external: true,
  },
  {
    icon: BookOpenCheck,
    title: "I want recognised development",
    body:
      "Explore our CPD and structured learning routes if you want to build evidence of professional development before applying, during training, or while progressing in a railway career.",
    cta: "View learning options",
    href: "https://traindriverfoundation.com/cpd",
    external: false,
  },
  {
    icon: Trophy,
    title: "I already work in rail",
    body:
      "If you are already employed in the railway and want to progress, develop operational thinking, strengthen interview evidence or move towards a driving role, we can support your next step.",
    cta: "Explore career support",
    href: "/contact",
    external: false,
  },
  {
    icon: UserCheck,
    title: "I am unsure where to start",
    body:
      "If you are not sure whether you need CV support, psychometric practice, interview preparation or structured learning, send an enquiry and we will direct you to the right part of the UKTDA ecosystem.",
    cta: "Ask for guidance",
    href: "/contact",
    external: false,
  },
];

const ECOSYSTEM_ROUTES = [
  {
    icon: Target,
    title: "Train Driver Foundation",
    label: "Best for applications, CVs, interviews and trainee driver preparation",
    body:
      "Train Driver Foundation is the candidate-facing home for aspiring train drivers. It supports individuals who want to understand the recruitment process, improve their applications, prepare for interviews and build a more credible route into the cab.",
    href: "https://traindriverfoundation.com",
    cta: "Visit Train Driver Foundation",
  },
  {
    icon: Gauge,
    title: "Train Driver Psychometrics UK",
    label: "Best for psychometric familiarisation and structured practice",
    body:
      "Train Driver Psychometrics UK focuses on ethical familiarisation and practice. It helps candidates understand timed assessment environments, track consistency and prepare with more structure before attending formal assessment.",
    href: "https://traindriverpsychometrics.co.uk",
    cta: "Visit Train Driver Psychometrics UK",
  },
  {
    icon: Route,
    title: "Railway Careers",
    label: "Best for finding rail opportunities and understanding routes into industry",
    body:
      "Railway Careers connects individuals with wider railway opportunities. It is useful if you are exploring rail employment, looking beyond train driving, or trying to understand where your background could fit within the industry.",
    href: "https://railwaycareers.co.uk",
    cta: "Visit Railway Careers",
  },
];

const PSYCHOMETRIC_AREAS = [
  {
    icon: Brain,
    title: "Attention, vigilance and concentration",
    body:
      "Train driver assessments commonly test a candidate’s ability to maintain focus, identify detail, work accurately and remain consistent over time. These are not just test skills; they reflect the demands of safety-critical work.",
  },
  {
    icon: Gauge,
    title: "Reaction, coordination and response control",
    body:
      "Candidates may face timed tasks that assess speed, accuracy, coordination and the ability to respond correctly under pressure. Practice should help you understand the environment and improve discipline, not encourage rushed or careless responses.",
  },
  {
    icon: ClipboardCheck,
    title: "Rules, reasoning and structured decision making",
    body:
      "Some preparation should also focus on how candidates process instructions, apply rules, avoid assumptions and make safe decisions. This is particularly important because train driving is built around procedural compliance and risk control.",
  },
];

const INDIVIDUAL_FAQS = [
  {
    q: "Where should I start if I want to become a train driver?",
    a:
      "Start by understanding the role and the recruitment process before buying lots of resources or applying everywhere at once. You need to know what operators are looking for: safety awareness, concentration, communication, rule-following, calm decision making, responsibility and motivation. A sensible first step is to review your CV and application evidence, then prepare for psychometric assessment and interview in a structured way.",
  },
  {
    q: "Can you guarantee that I will pass the train driver recruitment process?",
    a:
      "No. No ethical provider can guarantee that you will pass a train driver application, psychometric assessment or interview. These processes are controlled by train operators and assessment providers. What we can do is help you prepare more professionally, understand what is being assessed, avoid common mistakes and present your experience more clearly.",
  },
  {
    q: "What is the difference between Train Driver Foundation and Train Driver Psychometrics UK?",
    a:
      "Train Driver Foundation supports the wider candidate journey, including understanding the role, CVs, applications, interviews, operational thinking and personal development. Train Driver Psychometrics UK focuses specifically on psychometric familiarisation and structured practice for assessment-style skills such as vigilance, attention, reaction, perception and coordination.",
  },
  {
    q: "Are the psychometric practice tools the same as the real train driver tests?",
    a:
      "No. Practice tools should not copy live assessment materials. The purpose of ethical psychometric preparation is to build familiarity with the type of demands involved, such as timed working, sustained attention, accuracy, response control and consistency. This helps candidates prepare without compromising the integrity of formal assessment.",
  },
  {
    q: "I have no railway experience. Can I still become a train driver?",
    a:
      "Yes. Many successful train driver applicants come from outside the railway. What matters is whether you can evidence the right behaviours and aptitudes. Transferable experience from the military, emergency services, logistics, customer service, engineering, safety-critical work, shift work, control rooms and many other backgrounds can be relevant if explained properly.",
  },
  {
    q: "I already work in the railway. Do I still need preparation?",
    a:
      "Possibly. Railway experience can help, but it does not automatically make an application strong. You still need to evidence the specific behaviours required of a train driver, including concentration, compliance, safe communication, responsibility, route learning capability, incident awareness and the ability to make calm decisions in a safety-critical environment.",
  },
  {
    q: "Can younger people prepare before they are old enough to apply?",
    a:
      "Yes. Younger candidates can start developing the right mindset, language and evidence before they apply. This can include learning about the railway, developing operational maturity, building examples of responsibility, improving communication and understanding safety-critical decision making. The Future Driver pathway is designed for this early preparation stage.",
  },
  {
    q: "What should a train driver CV include?",
    a:
      "A train driver CV should be clear, concise and evidence-led. It should highlight safety-critical behaviours, reliability, concentration, communication, working under pressure, following rules, responsibility, shift work, customer awareness and relevant technical or operational experience. It should not simply say that you have always wanted to drive trains; it needs to show why you are suitable.",
  },
  {
    q: "What kind of interview questions do train driver applicants face?",
    a:
      "Candidates may be asked about motivation, safety, concentration, communication, responsibility, following rules, dealing with pressure, managing conflict, learning from mistakes and making decisions. Strong answers usually use real examples and explain what happened, what you did, why you acted that way and what the outcome was.",
  },
  {
    q: "Do your courses count as official train driver training?",
    a:
      "Our individual preparation and development resources are designed to support applicants and learners. They do not replace formal employer-led train driver training, operator competence management or statutory requirements. They are intended to help individuals prepare, develop and understand the standards expected in safety-critical railway roles.",
  },
  {
    q: "Which part of the UKTDA ecosystem is right for me?",
    a:
      "If you are applying for trainee driver roles, start with Train Driver Foundation. If your main concern is psychometric practice, use Train Driver Psychometrics UK. If you are exploring wider rail jobs, use Railway Careers. If you are unsure, send an enquiry and we will point you towards the most relevant support.",
  },
];

export default function ForIndividuals() {
  return (
    <>
      <SEO
        title="For Individuals | Train Driver Applications, Psychometrics, Interviews & Rail Career Support"
        description="Support for individuals who want to become train drivers or progress in rail. Explore Train Driver Foundation, Train Driver Psychometrics UK, Railway Careers, CV support, interview preparation, psychometric familiarisation and operational thinking."
        path="/for-individuals"
      />

      <PageHero
        eyebrow="For individuals"
        title="Your route into train driving and railway career development."
        intro="Whether you are applying for your first trainee train driver role, preparing for psychometric assessment, building interview evidence, planning ahead at 16–18, or already working in rail — this page will direct you to the right support."
      />

      <section className="py-20 md:py-28 bg-white" data-testid="individuals-intro">
        <div className="container-rail">
          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-10 lg:gap-16 items-start">
            <div>
              <p className="label-eyebrow text-safety">Individual learner support</p>

              <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                Clear, structured preparation for people who want to work in the railway.
              </h2>

              <p className="mt-5 text-slate-600 leading-relaxed">
                Applying to become a train driver can feel confusing. Different operators use
                different recruitment stages, vacancies can open and close quickly, and candidates
                often do not know whether they should focus on their CV, application form,
                psychometric preparation, interview examples or wider railway knowledge.
              </p>

              <p className="mt-4 text-slate-600 leading-relaxed">
                UKTDA brings those routes together. Through our wider ecosystem, individuals can
                access train driver application support, psychometric familiarisation, interview
                preparation, operational thinking courses, future driver development and wider rail
                career guidance.
              </p>

              <p className="mt-4 text-slate-600 leading-relaxed">
                The aim is simple: help you understand where you are now, what stage you are
                preparing for, and which support is most relevant. You do not need everything at
                once. You need the right preparation at the right point in your journey.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-md p-6 md:p-8">
              <p className="font-display font-bold text-navy text-xl">
                Not sure where to go first?
              </p>

              <p className="mt-3 text-slate-600 leading-relaxed">
                Use this simple guide:
              </p>

              <div className="mt-5 space-y-4">
                <div className="flex gap-3">
                  <CheckCircle2 className="h-5 w-5 text-safety flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-600 leading-relaxed">
                    <strong className="text-navy">Applications and interviews:</strong>{" "}
                    start with Train Driver Foundation.
                  </p>
                </div>

                <div className="flex gap-3">
                  <CheckCircle2 className="h-5 w-5 text-safety flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-600 leading-relaxed">
                    <strong className="text-navy">Psychometric practice:</strong>{" "}
                    use Train Driver Psychometrics UK.
                  </p>
                </div>

                <div className="flex gap-3">
                  <CheckCircle2 className="h-5 w-5 text-safety flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-600 leading-relaxed">
                    <strong className="text-navy">Wider rail jobs:</strong>{" "}
                    explore Railway Careers.
                  </p>
                </div>

                <div className="flex gap-3">
                  <CheckCircle2 className="h-5 w-5 text-safety flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-600 leading-relaxed">
                    <strong className="text-navy">Younger candidates:</strong>{" "}
                    follow the Future Driver pathway.
                  </p>
                </div>
              </div>

              <a
                href="/contact"
                data-testid="individuals-intro-contact"
                className="mt-6 inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-white font-semibold px-5 py-3 rounded-md transition-colors"
              >
                Ask us where to start <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-slate-50" data-testid="individuals-journey">
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">The individual journey</p>

            <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Train driver preparation is a journey, not a single download.
            </h2>

            <p className="mt-5 text-slate-600 leading-relaxed">
              Candidates often make the mistake of preparing only for the next visible hurdle.
              They download a CV template, practise a few tests or memorise interview answers.
              That can help in the short term, but stronger candidates usually prepare in a more
              structured way.
            </p>

            <p className="mt-4 text-slate-600 leading-relaxed">
              The railway is looking for people who can work safely, communicate clearly, follow
              rules, manage pressure and make disciplined decisions. The sections below show how
              the different parts of your preparation fit together.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            {JOURNEY_STEPS.map((step, index) => {
              const Icon = step.icon;

              return (
                <div
                  key={step.title}
                  className="bg-white border border-slate-200 rounded-md p-6 hover:border-safety/60 hover:shadow-sm transition-all"
                  data-testid={`individuals-journey-step-${index}`}
                >
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-navy text-white">
                    <Icon className="h-5 w-5" />
                  </div>

                  <p className="mt-4 font-display font-bold text-navy text-lg leading-snug">
                    {step.title}
                  </p>

                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                    {step.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white" data-testid="individuals-support-grid">
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">Choose your route</p>

            <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              What do you need help with today?
            </h2>

            <p className="mt-5 text-slate-600 leading-relaxed">
              This page is designed to help individuals move quickly to the most relevant part of
              the UKTDA ecosystem. Pick the option that best describes where you are now.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {INDIVIDUAL_SUPPORT.map((section, index) => {
              const Icon = section.icon;

              return (
                <div
                  key={section.title}
                  className="bg-white border border-slate-200 rounded-md p-6 hover:border-safety/60 hover:shadow-sm transition-all flex flex-col"
                  data-testid={`individual-support-${index}`}
                >
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-navy text-white">
                    <Icon className="h-5 w-5" />
                  </div>

                  <p className="mt-4 font-display font-bold text-navy text-lg">
                    {section.title}
                  </p>

                  <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-grow">
                    {section.body}
                  </p>

                  <a
                    href={section.href}
                    target={section.external ? "_blank" : undefined}
                    rel={section.external ? "noopener noreferrer" : undefined}
                    data-testid={`individual-support-cta-${index}`}
                    className="mt-5 inline-flex items-center gap-2 text-navy font-semibold hover:text-safety transition-colors"
                  >
                    {section.cta} <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-navy-deep text-white" data-testid="individuals-ecosystem">
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">UKTDA ecosystem</p>

            <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl text-white leading-tight">
              One ecosystem, different routes depending on your goal.
            </h2>

            <p className="mt-5 text-slate-300 leading-relaxed">
              UKTDA connects several specialist platforms. This helps individuals avoid the common
              problem of landing on the wrong page, buying the wrong product or preparing in the
              wrong order.
            </p>
          </div>

          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {ECOSYSTEM_ROUTES.map((route, index) => {
              const Icon = route.icon;

              return (
                <div
                  key={route.title}
                  className="bg-white/5 border border-white/10 rounded-md p-6 md:p-8"
                  data-testid={`individual-ecosystem-route-${index}`}
                >
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-safety text-white">
                    <Icon className="h-5 w-5" />
                  </div>

                  <h3 className="mt-5 font-display font-bold text-2xl text-white">
                    {route.title}
                  </h3>

                  <p className="mt-2 text-sm font-semibold text-safety">
                    {route.label}
                  </p>

                  <p className="mt-4 text-slate-300 leading-relaxed">
                    {route.body}
                  </p>

                  <a
                    href={route.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`individual-ecosystem-cta-${index}`}
                    className="mt-6 inline-flex items-center gap-2 bg-white text-navy hover:bg-slate-100 font-semibold px-5 py-3 rounded-md transition-colors"
                  >
                    {route.cta} <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white" data-testid="individuals-tdf-banner">
        <div className="container-rail">
          <div className="bg-navy-deep text-white rounded-md p-8 md:p-12 grid lg:grid-cols-[3fr_2fr] gap-10 items-center">
            <div>
              <p className="label-eyebrow text-safety">Specialist candidate preparation</p>

              <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl text-white leading-tight">
                Train Driver Foundation: structured support for aspiring train drivers.
              </h2>

              <p className="mt-4 text-slate-300 leading-relaxed">
                Train Driver Foundation is designed for people who want to become train drivers
                and need clear, practical preparation. It supports the individual candidate journey
                from early research through to applications, CV improvement, interviews,
                psychometric preparation and operational thinking.
              </p>

              <p className="mt-4 text-slate-300 leading-relaxed">
                This is the best place to start if your main goal is to apply for trainee train
                driver roles, understand the recruitment process, strengthen your evidence or
                prepare for interviews with more confidence.
              </p>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a
                  href="https://traindriverfoundation.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="individuals-cta-tdf"
                  className="inline-flex items-center justify-center gap-2 bg-safety hover:bg-[#e66e00] text-white font-semibold px-5 py-3 rounded-md transition-colors"
                >
                  Explore Train Driver Foundation <ArrowUpRight className="h-4 w-4" />
                </a>

                <a
                  href="/contact"
                  data-testid="individuals-cta-guidance"
                  className="inline-flex items-center justify-center gap-2 bg-white text-navy hover:bg-slate-100 font-semibold px-5 py-3 rounded-md transition-colors"
                >
                  Ask which support you need <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>

            <img
              src="/Traininplatform.jpg"
              alt="Train at a platform"
              loading="lazy"
              className="rounded-md w-full aspect-[4/3] object-cover opacity-90"
            />
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-slate-50" data-testid="individuals-psychometrics">
        <div className="container-rail">
          <div className="grid lg:grid-cols-[1fr_1.35fr] gap-10 lg:gap-14 items-start">
            <div>
              <p className="label-eyebrow text-safety">Psychometric familiarisation</p>

              <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                Prepare for assessment-style demands in a structured and ethical way.
              </h2>

              <p className="mt-4 text-slate-600 leading-relaxed">
                Train driver psychometric assessments are used because the role is
                safety-critical. Candidates may need to demonstrate concentration, vigilance,
                perception, response control, coordination, memory, rules understanding and the
                ability to work accurately under time pressure.
              </p>

              <p className="mt-4 text-slate-600 leading-relaxed">
                Train Driver Psychometrics UK is designed to support familiarisation. It does not
                claim to reproduce live assessment materials or provide shortcuts. Instead, it
                helps candidates understand the type of skills being explored, practise in a more
                disciplined way and track consistency over time.
              </p>

              <a
                href="https://traindriverpsychometrics.co.uk"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="individuals-cta-psychometrics"
                className="mt-6 inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-white font-semibold px-5 py-3 rounded-md transition-colors"
              >
                Explore Train Driver Psychometrics UK{" "}
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>

            <div className="grid gap-4">
              {PSYCHOMETRIC_AREAS.map((area, index) => {
                const Icon = area.icon;

                return (
                  <div
                    key={area.title}
                    className="bg-white border border-slate-200 rounded-md p-6"
                    data-testid={`individuals-psychometric-area-${index}`}
                  >
                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-navy text-white">
                      <Icon className="h-5 w-5" />
                    </div>

                    <p className="mt-4 font-display font-bold text-navy text-lg">
                      {area.title}
                    </p>

                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                      {area.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white" data-testid="individuals-final-funnel">
        <div className="container-rail">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-8 md:p-12">
            <div className="grid lg:grid-cols-[1fr_1fr] gap-10 items-start">
              <div>
                <p className="label-eyebrow text-safety">Next step</p>

                <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                  Still unsure what support you need?
                </h2>

                <p className="mt-4 text-slate-600 leading-relaxed">
                  You may not need a full course. You may need CV support, psychometric practice,
                  interview preparation, a clearer development plan, or simply guidance on which
                  route makes sense for your current stage.
                </p>

                <p className="mt-4 text-slate-600 leading-relaxed">
                  Send a short enquiry explaining where you are in the process and what you are
                  trying to achieve. We will direct you to the most relevant part of the ecosystem.
                </p>
              </div>

              <div className="bg-white border border-slate-200 rounded-md p-6">
                <p className="font-display font-bold text-navy text-xl">
                  Tell us:
                </p>

                <div className="mt-5 space-y-4">
                  <div className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-safety flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Are you applying now, preparing early, or already in rail?
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-safety flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Do you need help with CVs, applications, tests or interviews?
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-safety flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Have you already applied to a train operator?
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-safety flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Have you previously sat psychometric assessments?
                    </p>
                  </div>
                </div>

                <a
                  href="/contact"
                  data-testid="individuals-final-contact"
                  className="mt-6 inline-flex items-center gap-2 bg-safety hover:bg-[#e66e00] text-white font-semibold px-5 py-3 rounded-md transition-colors"
                >
                  Send an individual enquiry <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQSection
        items={INDIVIDUAL_FAQS}
        title="Train driver preparation: FAQs for individuals"
        intro="Clear answers for aspiring train drivers, younger candidates, career changers and existing rail employees preparing for applications, psychometric assessment and interview."
        schemaId="faq-individuals"
      />

      <CTASection
        eyebrow="Individual enquiry"
        title="Ready to take the next step towards a railway career?"
        intro="Tell us where you are in your journey and we’ll direct you to the right support — Train Driver Foundation, Train Driver Psychometrics UK, Railway Careers or another part of the UKTDA ecosystem."
        primary={{
          label: "Send enquiry",
          to: "/contact",
          testId: "individuals-cta-contact",
        }}
        secondary={{
          label: "Explore Train Driver Foundation",
          href: "https://traindriverfoundation.com",
          testId: "individuals-cta-tdf-footer",
        }}
      />
    </>
  );
}